/**
 * Яндекс ID: получение ssmm-токена двумя способами.
 *
 * backend (по умолчанию) — уходим редиректом на шлюз
 * `GET /oauth/yandex?redirect_uri=<эта страница>`: весь OAuth делает бэкенд
 * своими YANDEX_CLIENT_ID/SECRET и возвращает НАШУ страницу с уже готовым
 * ssmm-токеном в `?access_token=`. Со стороны Яндекса настраивать ничего не
 * нужно — там зарегистрирован callback шлюза. Так же работает gpt-boss-ext.
 *
 * direct — implicit-флоу самим фронтом по VITE_YANDEX_CLIENT_ID: Яндекс
 * возвращает СВОЙ access_token в hash, меняем его на ssmm-токен через
 * POST /oauth/yandex/authorize. Требует, чтобы в приложении Яндекса был
 * зарегистрирован Redirect URI этого origin.
 *
 * Разница только в том, что приходит назад; дальше оба пути одинаковы.
 */
import { getServiceName } from './serviceUtils';
import { sessionGet, sessionRemove, sessionSet } from './storage';
import { API_BASE } from './api';

export type YandexAuthMode = 'backend' | 'direct';

const CLIENT_ID = (import.meta.env.VITE_YANDEX_CLIENT_ID || '').trim();
const RAW_MODE = (import.meta.env.VITE_YANDEX_AUTH_MODE || '').trim().toLowerCase();

export const YANDEX_CLIENT_ID = CLIENT_ID;
export const YANDEX_API_BASE = API_BASE;

/** direct возможен только с client_id — без него молча остаёмся на серверном флоу. */
export const YANDEX_AUTH_MODE: YandexAuthMode = RAW_MODE === 'direct' && CLIENT_ID ? 'direct' : 'backend';

/** Метка «мы вернулись из Яндекса»: шлюз отдаёт query редиректа обратно как есть. */
const CALLBACK_MARKER = 'ssmm_yandex';
/** Одноразовый state (CSRF) — сверяем то, что вернулось из Яндекса. */
const STATE_KEY = 'ssmm-yandex-state';
/** Query исходной страницы: в implicit-флоу redirect_uri фиксированный, параметры теряются. */
const RESTORE_KEY = 'ssmm-yandex-restore';

const randomState = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* нет crypto — генерим попроще, это всего лишь одноразовая метка */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

/** Куда Яндекс вернёт пользователя в direct-режиме. Должен быть зарегистрирован в приложении Яндекса. */
const directRedirectUri = (): string => {
  const configured = (import.meta.env.VITE_YANDEX_REDIRECT_URI || '').trim();
  if (configured) return configured;
  return `${window.location.origin}${window.location.pathname}`;
};

/** Текущая страница со всеми её параметрами + метка возврата (шлюз сохраняет query редиректа). */
const backendRedirectUri = (): string => {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set(CALLBACK_MARKER, '1');
  return url.toString();
};

const describeError = (code?: string | null, description?: string | null): string => {
  if (description) return description;
  if (code === 'access_denied') return 'Вы отменили вход через Яндекс ID';
  return code ? `Яндекс вернул ошибку: ${code}` : 'Не удалось войти через Яндекс ID';
};

/** URL, на который нужно уйти, чтобы начать вход. */
export const buildYandexStartUrl = (): string => {
  const state = randomState();
  sessionSet(STATE_KEY, state);

  if (YANDEX_AUTH_MODE === 'direct') {
    // Яндекс вернёт нас на голый redirect_uri — параметры страницы (return_url,
    // mode, service) сохраняем сами, иначе после возврата некуда отдавать токен.
    sessionSet(RESTORE_KEY, window.location.search);
    const params = new URLSearchParams({
      response_type: 'token',
      client_id: CLIENT_ID,
      redirect_uri: directRedirectUri(),
      state,
    });
    return `https://oauth.yandex.ru/authorize?${params.toString()}`;
  }

  const params = new URLSearchParams({ redirect_uri: backendRedirectUri() });
  // service/utm шлюз читает из query: заголовки в браузерном редиректе не отправить.
  const service = getServiceName();
  if (service) params.set('service', service);
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (key === 'utm' || key.startsWith('utm_')) params.set(key, value);
  });

  return `${API_BASE}/oauth/yandex?${params.toString()}`;
};

export interface YandexCallback {
  mode: YandexAuthMode;
  /** backend-режим: готовый ssmm-токен, обменивать больше нечего. */
  ssmmToken?: string;
  /** direct-режим: access_token Яндекса, его ещё нужно обменять на ssmm-токен. */
  yandexToken?: string;
  error?: string;
}

/** Разбирает ответ Яндекса/шлюза в текущем URL. null — это обычное открытие страницы. */
export const readYandexCallback = (): YandexCallback | null => {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const expectedState = sessionGet(STATE_KEY);

  // implicit-ответ Яндекса всегда приходит в hash
  if (hash.has('access_token') || hash.has('error')) {
    if (hash.has('error')) {
      return { mode: 'direct', error: describeError(hash.get('error'), hash.get('error_description')) };
    }
    const returnedState = hash.get('state');
    if (expectedState && returnedState && expectedState !== returnedState) {
      return { mode: 'direct', error: 'Ответ Яндекса не совпал с запросом (state) — вход прерван. Попробуйте ещё раз.' };
    }
    return { mode: 'direct', yandexToken: hash.get('access_token') || undefined };
  }

  const hasMarker = Boolean(search.get(CALLBACK_MARKER));
  if (!hasMarker) {
    // Яндекс умеет вернуть отказ и в query — но только если мы сами его звали.
    if (search.has('error') && expectedState) {
      return { mode: YANDEX_AUTH_MODE, error: describeError(search.get('error'), search.get('error_description')) };
    }
    return null;
  }

  if (search.has('error') || search.has('error_code')) {
    return { mode: 'backend', error: describeError(search.get('error'), search.get('error_description')) };
  }

  const token = search.get('access_token');
  if (!token) {
    return { mode: 'backend', error: 'Шлюз не вернул токен авторизации' };
  }
  return { mode: 'backend', ssmmToken: token };
};

/**
 * Возвращает в URL параметры исходной страницы (return_url/mode/service), которые
 * потерялись на фиксированном redirect_uri implicit-флоу. Вызывать ДО рендера App:
 * весь код читает параметры из window.location.
 */
export const restoreYandexFlowParams = (): void => {
  const saved = sessionGet(RESTORE_KEY);
  if (!saved) return;

  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  if (!hash.has('access_token') && !hash.has('error')) return;

  sessionRemove(RESTORE_KEY);

  const url = new URL(window.location.href);
  new URLSearchParams(saved).forEach((value, key) => {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  });
  url.searchParams.set(CALLBACK_MARKER, '1');

  try {
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* без history пойдём дальше: токен всё равно лежит в hash */
  }
};

/** Убирает из адреса токен и служебные параметры, оставляя return_url/mode/service. */
export const clearYandexCallbackFromUrl = (): void => {
  sessionRemove(STATE_KEY);
  sessionRemove(RESTORE_KEY);

  const url = new URL(window.location.href);
  ['access_token', 'uid', 'role', 'error', 'error_code', 'error_description', CALLBACK_MARKER].forEach((key) => {
    url.searchParams.delete(key);
  });
  url.hash = '';

  try {
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  } catch {
    /* не смогли почистить адрес — не критично */
  }
};
